import {
	useState,
	useRef,
	ReactElement,
	Ref,
	useMemo,
	useEffect,
	useCallback,
	KeyboardEvent,
	RefObject
} from "react";
import { KeyEventArguments, TextInput } from "../TextInput/TextInput";
import {
	hsvToHex,
	cssColorToHsv,
	HsvColor,
	calculateRelativeLuminance,
	cssColorToRgb,
	RgbColor,
	getContrastRatio,
	rgbToHex
} from "client/Misc/colorFunctions";
import { localize } from "client_legacy/ndaw/culture";
import { DropOver } from "../DropOver/DropOver";
import { HsvPicker } from "./HsvPicker";
import styles from "./BaseColorPicker.module.less";
import {
	ensureNotNil,
	generateTrackingId,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import {
	MediaQueryKind,
	useIsTouchDevice,
	useStableImperativeCallback,
	useValueEqualMemo,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { ResponsiveMenu } from "../ResponsiveMenu/ResponsiveMenu";
import { never } from "client_legacy/ndaw/errors";
import classNames from "classnames";
import { useFocusTrap } from "client/Hooks/useFocusTrap";
import { IconButton } from "../IconButton/IconButton";
import { Key } from "client/Misc/commonTypes";

export type ColorContrastRequirements = {
	minimumRequiredContrastRatio: number;
	compareAgainst: readonly string[];
	message?: ColorContrastRequirementsMessage;
};

export type ColorContrastRequirementsMessage = {
	kind: "error" | "warning";
	text: string;
};

export type BaseColorPickerProps = {
	value: string | nil;
	onChange(value: string | undefined): void;
	trackingId: string;
	renderPreview(
		color: string | undefined,
		isOpen: boolean,
		clickHandler: () => void
	): ReactElement;
	contrastRequirements?: ColorContrastRequirements;
	menuTitle: string;
	onClosed?(): void;
	tooltip?: string;
	dropOverId: string;
	onFocusPreview: () => void;
};

export const BaseColorPicker = (props: BaseColorPickerProps): ReactElement => {
	const {
		value: valueFromProps,
		onChange: onChangeFromProps,
		trackingId,
		renderPreview,
		contrastRequirements,
		menuTitle,
		onClosed,
		tooltip,
		dropOverId,
		onFocusPreview
	} = props;

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [persistedHsvColor, setPersistedHsvColor] = useState<ColorPickerState>({ hsv: undefined, hex: undefined });
	const containerRef: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);

	useFocusTrap<HTMLDivElement>(containerRef);

	const onChange: typeof onChangeFromProps = useStableImperativeCallback(onChangeFromProps);

	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);
	const isTouch: boolean = useIsTouchDevice();

	const hsvColorFromProp: HsvColor | undefined = useMemo(
		() => cssColorToHsv(valueFromProps),
		[valueFromProps]
	);

	let hsvColor: HsvColor | undefined = hsvColorFromProp;

	// We have this weird construction here because multiple hsv values map to the same rgb values.
	// Without this, the hsv values would change / go out of sync with what the user is selecting
	// because the resulting rgb values map to a different hsv combination. This workaround makes
	// it that if the same rgb value is given, it takes the previous hsv values to keep in sync
	// with the user actually selected.
	if (valueFromProps?.trim() && persistedHsvColor) {
		if (!hsvColor
			|| persistedHsvColor.hex === valueFromProps.trim()
		) {
			hsvColor = persistedHsvColor.hsv;
		}
	}

	const setHsvColor = useCallback(
		(hsv: HsvColor | undefined): void => {
			const hex: string | undefined = hsvToHex(hsv);
			setPersistedHsvColor({ hsv, hex });
			onChange(hex);
		},
		[onChange]
	);

	useEffect(
		(): void => {
			if (hsvColor
				&& isNotNil(valueFromProps)
				&& valueFromProps?.length >= 1
				&& valueFromProps[0] !== "#"
			) {
				onChange(
					hsvToHex(hsvColor)
				);
			}
		},
		[
			hsvColor,
			onChange,
			valueFromProps
		]
	);

	const previewColor: string | undefined = hsvToHex(hsvColorFromProp);

	const ref: Ref<HTMLDivElement> = useRef<HTMLDivElement>(null);

	const luminancesForContrastCalculation = useValueEqualMemo(
		(): (readonly number[] | undefined) => {
			if (!contrastRequirements || contrastRequirements.compareAgainst.length === 0){
				return undefined;
			}

			return contrastRequirements.compareAgainst
				.map(
					x => calculateRelativeLuminance(
						ensureNotNil(
							cssColorToRgb(x)
						)
					)
				);
		},
		[contrastRequirements]
	);

	const contrastRatios = useMemo(
		(): (readonly number[] | undefined) => {
			if (!luminancesForContrastCalculation) {
				return undefined;
			}

			const rgb: RgbColor | undefined = cssColorToRgb(valueFromProps ?? undefined);
			if (!rgb) {
				return undefined;
			}

			return luminancesForContrastCalculation.map(
				x => getContrastRatio(rgb, x)
			);
		},
		[luminancesForContrastCalculation, valueFromProps]
	);

	const textInputChangeHandler = useCallback(
		(value: string): void => {
			if (value.length >= 1 && value[0] !== "#") {
				const convertedColor: RgbColor | undefined = cssColorToRgb(value);

				if (convertedColor) {
					value = rgbToHex(convertedColor);
				}
			}

			if (value.length > 0 && value[0] !== "#") {
				value = "#" + value;
			}

			onChange(value);
		},
		[onChange]
	);

	const closeHandler = useCallback(
		(): void => {
			setIsOpen(false);
			onClosed?.();
		},
		[onClosed]
	);

	const toggleHandler = useCallback(
		(): void => {
			if (isOpen) {
				setIsOpen(false);
				onClosed?.();
			} else {
				setIsOpen(true);
			}
		},
		[
			isOpen,
			onClosed
		]
	);

	const doesSatisfyContrastRequirements: boolean = isNotNil(contrastRequirements)
		&& isNotNil(contrastRatios)
		&& contrastRatios.length > 0
		&& contrastRatios.every(x => x >= contrastRequirements.minimumRequiredContrastRatio);

	let valueForTextInput: string = valueFromProps ?? "";

	if (valueForTextInput.length > 1 && valueForTextInput[0] === "#") {
		valueForTextInput = valueForTextInput.substring(1);
	}

	const dropOverKeyUpHandler = useCallback(
		(args: KeyboardEvent): void => {
			switch (args.key) {
				case Key.Escape:
					closeHandler();
					onFocusPreview();
					break;
				default:
					return;
			}
		},
		[closeHandler, onFocusPreview]
	);

	const colorSelectionKeyHandler = useCallback(
		(args: KeyboardEvent | KeyEventArguments): boolean => {
			switch (args.key) {
				case Key.Enter:
					closeHandler();
					onFocusPreview?.();
					return true;
				default:
					return false;
			}
		},
		[closeHandler, onFocusPreview]
	);

	const outsideClickIgnoreRefs = useMemo(
		(): readonly RefObject<HTMLElement | null>[]=> {
			return [ref];
		},
		[ref]
	);

	const renderContent = (): ReactElement => {
		return (
			<div
				className={styles.content}
				ref={containerRef}
			>
				<div className={styles.inputContainer}>
					<div className={styles.hashtag}>
						#
					</div>
					<label
						className={styles.input}
						aria-label={localize("_Hex")}
					>
						<TextInput
							isBorderless
							value={valueForTextInput}
							onChange={textInputChangeHandler}
							trackingId={generateTrackingId(trackingId, "__input")}
							autoCapitalize="none"
							autoFocus={!isTouch ? "focus" : undefined}
							onKeyDown={colorSelectionKeyHandler}
						/>
					</label>

					<IconButton
						icon="trash-can"
						kind="transparent"
						trackingId={generateTrackingId(trackingId, "__clear")}
						onClick={() => setHsvColor(undefined)}
						tooltip={localize("_Clear")}
					/>
				</div>

				<HsvPicker
					value={hsvColor}
					onChange={setHsvColor}
					trackingId={generateTrackingId(trackingId, "__hsv")}
					onSelectorsKeyUp={colorSelectionKeyHandler}
				/>
				{isNotNil(valueFromProps) && isNotNil(contrastRequirements) && (
					<ContrastRequirementsMessage
						isSatisfied={doesSatisfyContrastRequirements}
						message={contrastRequirements.message}
					/>
				)}
			</div>
		);
	};

	return (
		<div
			className={styles.root}
			ref={ref}
			{...setTrackingId(trackingId)}
			title={tooltip}
		>
			{renderPreview(
				previewColor,
				isOpen,
				toggleHandler
			)}

			{isOpen && ref.current && (
				<DropOver
					target={ref.current}
					onClose={closeHandler}
					outsideClickIgnoreRefs={outsideClickIgnoreRefs}
					closeOnMouseDown
					id={dropOverId}
					role="dialog"
					onKeyUp={dropOverKeyUpHandler}
				>
					{isResponsive
						? (
							<ResponsiveMenu
								trackingId={generateTrackingId(trackingId, "__dropover")}
								onClose={closeHandler}
							>
								<ResponsiveMenu.Header title={menuTitle} />

								<ResponsiveMenu.Body>
									{renderContent()}
								</ResponsiveMenu.Body>
							</ResponsiveMenu>
						)
						: (
							<div
								{...setTrackingId(trackingId, "__dropover")}
								className={styles.dropover}
							>
								{renderContent()}
							</div>
						)
					}
				</DropOver>
			)}
		</div>
	);
};
BaseColorPicker.displayName="BaseColorPicker";

type ColorPickerState = {
	hsv: HsvColor | undefined;
	hex: string | undefined;
};

type ContrastRequirementsMessageProps = {
	isSatisfied: boolean;
	message?: ColorContrastRequirementsMessage;
};

const ContrastRequirementsMessage = (props: ContrastRequirementsMessageProps): ReactElement => {
	const {
		isSatisfied,
		message
	} = props;

	const notSatisfiedMessage = useMemo(
		(): ColorContrastRequirementsMessage => message
		?? {
			kind: "error",
			text: localize("_TheSelectedColorDoesntMeetAccessibilityStandardsPleaseSelectInfo")
		},
		[message]
	);

	return (
		<div
			className={classNames(
				styles.contrastRequirements,
				getStyle(isSatisfied, notSatisfiedMessage)
			)}
			aria-live="assertive"
		>
			{isSatisfied
				? localize("_TheSelectedColorMeetsAccessibilityStandards")
				: notSatisfiedMessage.text
			}
		</div>
	);
};

function getStyle(
	isSatisfied: boolean,
	notSatisfiedMessage: ColorContrastRequirementsMessage
): string {
	if (isSatisfied) {
		return styles.contrastRequirements_success;
	}

	switch (notSatisfiedMessage.kind) {
		case "error":
			return styles.contrastRequirements_error;
		case "warning":
			return styles.contrastRequirements_warning;
		default:
			never(notSatisfiedMessage.kind);
	}
}